import React, { useEffect, useState } from 'react';
import Footer from './common/home/Footer';
import TopBar from './common/home/TopBar';
import { styles } from './common/Styles';
import { Divider, FormControlLabel, Grid, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography } from '@mui/material';
import { homeBackground } from './common/ColorCodes';
import { CONST_SITE_LABEL } from './common/Helper';

export const DataCentreCalculator = () => {

  const [config, setConfig] = useState({
    serversPerRack: 10,
    coresPerCPU: 20,
    cpusPerServer: 2,
    hyperthreadingEnabled: true,
    storagePerServer: 2000, // GB per server (2TB)
    ssdCostPerTB: 150, // Cost per TB for enterprise SSD
    networkBandwidth: 200, // Mbps
    monthlyNetworkTransfer: 66, // TB per month
    storageCostPerTB: 200, // Cost per TB
    networkEgressPerMonth: 1000, // GB per month
    networkEgressCostPerTB: 50, // Cost per TB of egress
    serverCost: 5000,
    rackCost: 1000,
    switchCost: 2000,
    powerCost: 3000,
    coolingCost: 5000,
    accessoriesCost: 1000,
    yearlyPowerCost: 4400,
    yearlyCoolingCost: 880,
    yearlyNetworkCost: 1200,
    yearlyMaintenanceCost: 3100,
    yearlyHostingCost: 6000,
    lifecycleYears: 5,
    awsInstanceType: 'c5.2xlarge', // Default instance type
    awsInstanceCost: 0.17, // Cost per hour for the instance
    awsStorageCostPerGB: 0.10, // EBS gp3 storage cost per GB per month
    awsNetworkEgressCostPerGB: 0.09, // Network egress cost per GB
    awsReservedInstanceDiscount: 40, // Percentage discount for RI
  });

  const calculateCosts = () => {
    const vCPUsPerServer = config.coresPerCPU * config.cpusPerServer * (config.hyperthreadingEnabled ? 2 : 1);
    const totalVCPUs = vCPUsPerServer * config.serversPerRack;

    // Calculate storage costs with more detail
    const totalStorageTB = (config.storagePerServer * config.serversPerRack) / 1000; // Convert GB to TB
    const totalStorageCapEx = totalStorageTB * config.ssdCostPerTB;
    const monthlyStorageCost = totalStorageCapEx / (config.lifecycleYears * 12);
    const costPerTBPerMonth = monthlyStorageCost / totalStorageTB;

    // Calculate network costs with more detail
    const monthlyNetworkCost = config.monthlyNetworkTransfer * config.networkEgressCostPerTB;
    const costPerMbps = monthlyNetworkCost / config.networkBandwidth;
    const costPerTBTransfer = monthlyNetworkCost / config.monthlyNetworkTransfer;

    // Hardware CapEx
    const totalHardwareCost =
      (config.serverCost * config.serversPerRack) +
      config.rackCost +
      config.switchCost +
      config.powerCost +
      config.coolingCost +
      config.accessoriesCost +
      totalStorageCapEx;

    // Yearly OpEx
    const yearlyOpEx =
      config.yearlyPowerCost +
      config.yearlyCoolingCost +
      config.yearlyNetworkCost +
      config.yearlyMaintenanceCost +
      config.yearlyHostingCost +
      (monthlyNetworkCost * 12);

    // Total TCO
    const totalTCO = totalHardwareCost + (yearlyOpEx * config.lifecycleYears);

    // vCPU hours in lifecycle
    const totalVCPUHours = totalVCPUs * 24 * 365 * config.lifecycleYears;

    // Cost per vCPU
    const costPerVCPUHour = totalTCO / totalVCPUHours;
    const costPerVCPUMonth = costPerVCPUHour * 24 * 30;

    // Calculate AWS costs
    const awsInstanceVCPUs = 8; // c5.2xlarge has 8 vCPUs
    const awsInstancesNeeded = Math.ceil(totalVCPUs / awsInstanceVCPUs);

    // Calculate AWS costs
    const awsMonthlyInstanceCost = awsInstancesNeeded * config.awsInstanceCost * 24 * 30;
    const awsYearlyInstanceCost = awsMonthlyInstanceCost * 12;

    // Apply RI discount
    const awsYearlyInstanceCostWithRI = awsYearlyInstanceCost * (1 - config.awsReservedInstanceDiscount / 100);

    // AWS Storage costs
    const awsMonthlyStorageCost = totalStorageTB * 80; // $80 per TB per month for EBS gp3
    const awsYearlyStorageCost = awsMonthlyStorageCost * 12;

    // Network egress costs
    const awsYearlyNetworkEgressCost = config.monthlyNetworkTransfer * 12 * config.awsNetworkEgressCostPerGB * 1000;

    // Total AWS yearly cost
    const awsYearlyTotalCost = awsYearlyInstanceCostWithRI + awsYearlyStorageCost + awsYearlyNetworkEgressCost;
    const awsTotalCost = awsYearlyTotalCost * config.lifecycleYears;

    // Cost per vCPU calculations for AWS
    const awsTotalVCPUHours = awsInstancesNeeded * awsInstanceVCPUs * 24 * 365 * config.lifecycleYears;
    const awsCostPerVCPUHour = awsTotalCost / awsTotalVCPUHours;
    const awsCostPerVCPUMonth = awsCostPerVCPUHour * 24 * 30;

    return {
      totalVCPUs,
      totalStorageTB,
      totalStorageCapEx,
      monthlyStorageCost,
      costPerTBPerMonth,
      monthlyNetworkCost,
      costPerMbps,
      costPerTBTransfer,
      totalHardwareCost,
      yearlyOpEx,
      totalTCO,
      costPerVCPUHour,
      costPerVCPUMonth,
      awsInstancesNeeded,
      awsYearlyTotalCost,
      awsTotalCost,
      awsCostPerVCPUHour,
      awsCostPerVCPUMonth,
      awsStorageCostPerTB: 80, // Fixed AWS EBS gp3 cost
      storageCostDifference: ((awsMonthlyStorageCost - monthlyStorageCost) / awsMonthlyStorageCost * 100).toFixed(2),
      costSavingsPercentage: ((awsTotalCost - totalTCO) / awsTotalCost * 100).toFixed(2),
    };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : Number(value),
    }));
  };

  const results = calculateCosts();

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Data Centre Cost Calculator`
  }, [])

  return (
    <div style={{ backgroundColor: homeBackground }}>
      <TopBar data-testid="top-bar-sticky" />

      <div>
        <img src='/image4.png' alt='About us banner' width={'100%'} />
      </div>

      <Typography variant="h3" component="h1" gutterBottom align="center" paddingTop={'20px'}>
        Data Center Cost Calculator
      </Typography>

      <Grid container spacing={3} style={{padding: '20px 50px'}}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom >
              Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Servers per Rack"
                  name="serversPerRack"
                  type="number"
                  value={config.serversPerRack}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cores per CPU"
                  name="coresPerCPU"
                  type="number"
                  value={config.coresPerCPU}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CPUs per Server"
                  name="cpusPerServer"
                  type="number"
                  value={config.cpusPerServer}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Server Cost ($)"
                  name="serverCost"
                  type="number"
                  value={config.serverCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rack Cost ($)"
                  name="rackCost"
                  type="number"
                  value={config.rackCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Switch Cost ($)"
                  name="switchCost"
                  type="number"
                  value={config.switchCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Power Cost ($)"
                  name="powerCost"
                  type="number"
                  value={config.powerCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cooling Cost ($)"
                  name="coolingCost"
                  type="number"
                  value={config.coolingCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Accessories Cost ($)"
                  name="accessoriesCost"
                  type="number"
                  value={config.accessoriesCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Yearly Power Cost ($)"
                  name="yearlyPowerCost"
                  type="number"
                  value={config.yearlyPowerCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Yearly Cooling Cost ($)"
                  name="yearlyCoolingCost"
                  type="number"
                  value={config.yearlyCoolingCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Yearly Network Cost ($)"
                  name="yearlyNetworkCost"
                  type="number"
                  value={config.yearlyNetworkCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Yearly Maintenance Cost ($)"
                  name="yearlyMaintenanceCost"
                  type="number"
                  value={config.yearlyMaintenanceCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Yearly Hosting Cost ($)"
                  name="yearlyHostingCost"
                  type="number"
                  value={config.yearlyHostingCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lifecycle Years"
                  name="lifecycleYears"
                  type="number"
                  value={config.lifecycleYears}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Storage Configuration
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Storage per Server (GB)"
                  name="storagePerServer"
                  type="number"
                  value={config.storagePerServer}
                  onChange={handleInputChange}
                  helperText="Enterprise SSD storage per server"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SSD Cost per TB ($)"
                  name="ssdCostPerTB"
                  type="number"
                  value={config.ssdCostPerTB}
                  onChange={handleInputChange}
                  helperText="Cost per TB for enterprise SSDs"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Network Configuration
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Network Bandwidth (Mbps)"
                  name="networkBandwidth"
                  type="number"
                  value={config.networkBandwidth}
                  onChange={handleInputChange}
                  helperText="Average utilized bandwidth"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monthly Network Transfer (TB)"
                  name="monthlyNetworkTransfer"
                  type="number"
                  value={config.monthlyNetworkTransfer}
                  onChange={handleInputChange}
                  helperText="Monthly data transfer"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.hyperthreadingEnabled}
                      onChange={handleInputChange}
                      name="hyperthreadingEnabled"
                    />
                  }
                  label="Enable Hyperthreading"
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h5" gutterBottom>
              AWS Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="AWS Instance Type"
                  name="awsInstanceType"
                  value={config.awsInstanceType}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instance Cost per Hour ($)"
                  name="awsInstanceCost"
                  type="number"
                  value={config.awsInstanceCost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Storage Cost per GB/Month ($)"
                  name="awsStorageCostPerGB"
                  type="number"
                  value={config.awsStorageCostPerGB}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Network Egress Cost per GB ($)"
                  name="awsNetworkEgressCostPerGB"
                  type="number"
                  value={config.awsNetworkEgressCostPerGB}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reserved Instance Discount (%)"
                  name="awsReservedInstanceDiscount"
                  type="number"
                  value={config.awsReservedInstanceDiscount}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Results
            </Typography>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">Total vCPUs</TableCell>
                    <TableCell align="right">{results.totalVCPUs}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Total Storage (TB)</TableCell>
                    <TableCell align="right">{results.totalStorageTB.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Total Hardware Cost</TableCell>
                    <TableCell align="right">${results.totalHardwareCost.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Yearly Operating Cost</TableCell>
                    <TableCell align="right">${results.yearlyOpEx.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Total TCO (5 years)</TableCell>
                    <TableCell align="right">${results.totalTCO.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Cost per vCPU-hour</TableCell>
                    <TableCell align="right">${results.costPerVCPUHour.toFixed(4)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Cost per vCPU-month</TableCell>
                    <TableCell align="right">${results.costPerVCPUMonth.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Monthly Network Cost</TableCell>
                    <TableCell align="right">${results.monthlyNetworkCost.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Cost per Mbps</TableCell>
                    <TableCell align="right">${results.costPerMbps.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Cost per TB Transfer</TableCell>
                    <TableCell align="right">${results.costPerTBTransfer.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="h6" align="center" sx={{ my: 1 }}>
                        Storage Cost Analysis
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Total Storage Capacity</TableCell>
                    <TableCell align="right">{results.totalStorageTB.toFixed(2)} TB</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Storage CapEx</TableCell>
                    <TableCell align="right">${results.totalStorageCapEx.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Monthly Storage Cost</TableCell>
                    <TableCell align="right">${results.monthlyStorageCost.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Cost per TB per Month</TableCell>
                    <TableCell align="right">${results.costPerTBPerMonth.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">AWS Storage Cost per TB</TableCell>
                    <TableCell align="right">${results.awsStorageCostPerTB}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Storage Cost Savings vs AWS</TableCell>
                    <TableCell align="right" sx={{
                      color: 'success.main',
                      fontWeight: 'bold'
                    }}>
                      {results.storageCostDifference}% cheaper
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="h6" align="center" sx={{ my: 1 }}>
                        AWS Comparison
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">AWS Instances Needed</TableCell>
                    <TableCell align="right">{results.awsInstancesNeeded}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">AWS Yearly Cost</TableCell>
                    <TableCell align="right">${results.awsYearlyTotalCost.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">AWS Total Cost ({config.lifecycleYears} years)</TableCell>
                    <TableCell align="right">${results.awsTotalCost.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">AWS Cost per vCPU-hour</TableCell>
                    <TableCell align="right">${results.awsCostPerVCPUHour.toFixed(4)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">AWS Cost per vCPU-month</TableCell>
                    <TableCell align="right">${results.awsCostPerVCPUMonth.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="h6" align="center" sx={{ my: 1 }}>
                        Cost Comparison
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">On-Prem vs AWS Cost Difference</TableCell>
                    <TableCell align="right" sx={{
                      color: results.totalTCO < results.awsTotalCost ? 'success.main' : 'error.main',
                      fontWeight: 'bold'
                    }}>
                      {results.totalTCO < results.awsTotalCost ? 'Saves' : 'Costs'} ${Math.abs(results.totalTCO - results.awsTotalCost).toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Cost Savings Percentage</TableCell>
                    <TableCell align="right" sx={{
                      color: results.costSavingsPercentage > 0 ? 'success.main' : 'error.main',
                      fontWeight: 'bold'
                    }}>
                      {results.costSavingsPercentage}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Footer />
    </div>
  )
}
